import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface AssistantTable {
  kind: 'table';
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  rowLinks?: (string | null)[];
  total: number;
}

export type ActionStatus = 'VORGESCHLAGEN' | 'BESTAETIGT' | 'ABGELEHNT' | 'ABGELAUFEN' | 'FEHLGESCHLAGEN';

export interface AssistantAction {
  id: string;
  type: string;
  status: ActionStatus;
  preview: { title: string; lines: [string, string][]; link?: string | null; warning?: string | null };
  expiresAt: string;
  decidedAt: string | null;
  result: { message: string; link?: string | null } | null;
  error: string | null;
}

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
  tables: AssistantTable[];
  actions: AssistantAction[];
  provider: string | null;
  notice: string | null;
  /** nur lokal: noch nicht gespeichert */
  pending?: boolean;
  error?: boolean;
}

export interface AssistantConversation {
  id: string;
  title: string | null;
  lastActivityAt: string;
  messages: AssistantMessage[];
}

export interface AssistantStatus {
  mode: 'openai' | 'regelbasiert';
  model: string | null;
  tools: string[];
  suggestions: string[];
  notice: string;
}

const CONV_KEY = ['assistant', 'conversation'] as const;

/** Gemeinsamer Zustand für Chat-Panel und Assistent-Seite (über den Query-Cache synchron) */
export function useAssistant(enabled = true) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['assistant', 'status'], queryFn: () => api.get<AssistantStatus>('/assistant/status'), staleTime: 5 * 60_000, enabled });
  const conversation = useQuery({
    queryKey: CONV_KEY,
    queryFn: async () => (await api.get<{ conversation: AssistantConversation | null }>('/assistant/conversation')).conversation,
    staleTime: 30_000,
    enabled,
  });

  const setConv = (fn: (c: AssistantConversation | null) => AssistantConversation | null) => qc.setQueryData<AssistantConversation | null>(CONV_KEY, (c) => fn(c ?? null));

  const send = useMutation({
    mutationFn: (text: string) =>
      api.post<{ conversationId: string; messages: AssistantMessage[] }>('/assistant/messages', { text, conversationId: qc.getQueryData<AssistantConversation | null>(CONV_KEY)?.id || null }),
    onMutate: (text) => {
      const temp: AssistantMessage = { id: `tmp-${Date.now()}`, role: 'user', text, createdAt: new Date().toISOString(), tables: [], actions: [], provider: null, notice: null, pending: true };
      setConv((c) => (c ? { ...c, messages: [...c.messages, temp] } : { id: '', title: null, lastActivityAt: temp.createdAt, messages: [temp] }));
      return { tempId: temp.id };
    },
    onSuccess: (res, _text, ctx) => {
      setConv((c) => ({
        id: res.conversationId,
        title: c?.title ?? null,
        lastActivityAt: new Date().toISOString(),
        messages: [...(c?.messages ?? []).filter((m) => m.id !== ctx?.tempId), ...res.messages],
      }));
    },
    onError: (_e, _text, ctx) => {
      setConv((c) => (c ? { ...c, messages: c.messages.map((m) => (m.id === ctx?.tempId ? { ...m, pending: false, error: true } : m)) } : c));
    },
  });

  const patchAction = (id: string, patch: Partial<AssistantAction>) =>
    setConv((c) =>
      c ? { ...c, messages: c.messages.map((m) => (m.actions.some((a) => a.id === id) ? { ...m, actions: m.actions.map((a) => (a.id === id ? { ...a, ...patch } : a)) } : m)) } : c,
    );

  const confirm = useMutation({
    mutationFn: (id: string) =>
      api.post<{ status: ActionStatus; result?: AssistantAction['result']; error?: string }>(`/assistant/actions/${id}/confirm`),
    onSuccess: (r, id) => {
      patchAction(id, { status: r.status, result: r.result ?? null, error: r.error ?? null, decidedAt: new Date().toISOString() });
      // Fachdaten (Zahlungen, Mahnungen, Aufgaben, Dashboard) neu laden
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'assistant' });
    },
    onError: () => void qc.invalidateQueries({ queryKey: CONV_KEY }),
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.post<{ status: ActionStatus }>(`/assistant/actions/${id}/reject`),
    onSuccess: (r, id) => patchAction(id, { status: r.status, decidedAt: new Date().toISOString() }),
    onError: () => void qc.invalidateQueries({ queryKey: CONV_KEY }),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post<{ conversation: AssistantConversation }>('/assistant/conversations')).conversation,
    onSuccess: (c) => qc.setQueryData(CONV_KEY, c),
  });

  return { status, conversation, send, confirm, reject, reset };
}
